import { useRef, useState } from "react";
import { ImagePlus, RefreshCw, Trash2 } from "lucide-react";
import { IMAGE_TYPES, MAX_IMAGE_BYTES, uploadQuestionImage } from "../api/worker";

/**
 * Pick a question image from the computer: checked here (an image, 5 MB or
 * less), sent to storage, and handed back as its link. The parent saves that
 * link on the question, so nothing is stored until the question is saved.
 */
export function ImageUpload({
  value,
  onChange,
  label = "Image",
  required = false,
  disabled = false,
}: {
  /** The current image link, or "" for none. */
  value: string;
  onChange: (url: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setError("Choose a PNG, JPEG, WebP, GIF or SVG image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(
        `That image is ${(file.size / 1024 / 1024).toFixed(1)} MB. Images must be 5 MB or smaller.`,
      );
      return;
    }
    setBusy(true);
    try {
      onChange(await uploadQuestionImage(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't upload the image.");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const id = `image-upload-${label.replace(/\W+/g, "-").toLowerCase()}`;

  return (
    <div>
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
        {label}
        {required ? " *" : ""}
      </span>
      <input
        ref={input}
        id={id}
        type="file"
        accept={IMAGE_TYPES.join(",")}
        className="sr-only"
        disabled={disabled || busy}
        aria-label={`Upload ${label.toLowerCase()}`}
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      {value ? (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-3 sm:flex-row sm:items-start">
          <img
            src={value}
            alt={label}
            className="max-h-48 rounded border border-border bg-white object-contain"
          />
          {disabled ? null : (
            <div className="flex gap-2 sm:flex-col">
              <label
                htmlFor={id}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-semibold hover:border-accent"
              >
                <RefreshCw size={13} aria-hidden="true" /> {busy ? "Uploading…" : "Replace"}
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => onChange("")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-semibold text-negative hover:border-negative disabled:opacity-50"
              >
                <Trash2 size={13} aria-hidden="true" /> Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        <label
          htmlFor={id}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!disabled && !busy) void pick(e.dataTransfer.files[0]);
          }}
          className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-border p-5 text-center ${
            disabled ? "opacity-50" : "cursor-pointer hover:border-accent"
          }`}
        >
          <ImagePlus size={20} className="text-text-muted" aria-hidden="true" />
          <span className="text-[13px] font-semibold">
            {busy ? "Uploading…" : "Upload an image, or drop it here"}
          </span>
          <span className="text-[11px] text-text-muted">
            PNG, JPEG, WebP, GIF or SVG · up to 5 MB
          </span>
        </label>
      )}
      {error ? (
        <p role="alert" className="mt-1.5 text-[12px] text-negative">
          {error}
        </p>
      ) : null}
    </div>
  );
}
